// CalendarWeekBar.tsx
import * as amplitude from '@amplitude/analytics-browser';
import {
  BulbFilled,
  CalendarOutlined,
  CheckOutlined,
  DownOutlined,
  InfoCircleFilled,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Calendar, Collapse, Empty, Modal } from 'antd';
import Drop from 'assets/drop.svg?react';
import GGButton from 'components/buttons/GGButton';
import SymptomTag from 'components/SymptomTag';
import AppColors from 'config/AppColors';
import dayjs, { Dayjs } from 'dayjs';
import usePreferences from 'hooks/usePreferences';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';

const useStyles = createUseStyles({
  weekBar: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  dayContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 32,
  },
  dayLabel: {
    fontSize: 14,
    color: AppColors.text.veryLightGray,
    marginBottom: 2,
    textTransform: 'capitalize',
  },
  date: {
    fontSize: 18,
    color: AppColors.text.lightGray,
    fontWeight: 400,
  },
  dateToday: {
    color: AppColors.colorTextBase,
    fontWeight: 700,
  },
  datePast: {
    color: AppColors.colorTextBase,
  },
  dot: {
    marginTop: 4,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: AppColors.midGrey,
  },
  dotRed: {
    composes: '$dot',
    background: AppColors.colorMap.veryBad,
  },
  dotGreen: {
    composes: '$dot',
    background: AppColors.main,
  },
  dotRedOutlined: {
    composes: '$dot',
    border: '1px solid #c62828',
  },
  calendarIconContainer: {
    all: 'unset',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    color: '#4db6ac',
    fontSize: 24,
    width: 32,
    cursor: 'pointer',
  },
  calendarCollapse: {
    '& .ant-collapse-content-box': {
      padding: '0 !important',
      marginBottom: 24,
    },
    '& .ant-collapse-header': {
      padding: '0 !important',
    },
  },
  calendar: {
    background: 'none',
    '& .ant-picker-panel': {
      border: 'none',
    },
    '& .ant-picker-date-panel': {
      backgroundColor: AppColors.backgroundGrey,
    },
    '& .ant-picker-body thead': {
      display: 'none',
    },
    '& .ant-picker-cell': {
      padding: 1,
      backgroundColor: 'white',
      border: `solid 3px ${AppColors.backgroundGrey}`,
    },
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textTransform: 'capitalize',
    fontWeight: 'bold',
    fontSize: 17,
    color: AppColors.text.header,
  },
  dateCellContainer: {
    aspectRatio: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontSize: 18,
  },
  dateNumberOtherMonth: { color: AppColors.text.veryLightGray },
  dateNumberPast: {},
  dateNumberToday: { fontWeight: 'bold' },
  dateNumberFuture: { color: AppColors.text.lightGray },
  dayDots: { display: 'flex', flexDirection: 'row' },
  dateCellTopRow: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginBottom: 3,
    paddingRight: 4,
    height: 3,
    fontSize: 10,
    color: AppColors.text.veryLightGray,
    width: '100%',
    position: 'relative',
    top: 4,
  },
  dateCellBottomRow: { display: 'flex', flexDirection: 'row' },
  dateCellBottomRowWhenFillingPeriod: {
    composes: '$dateCellBottomRow',
    height: 10,
    '& svg': {
      height: 10,
      width: 10,
    },
  },
  calendarHelper: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: AppColors.text.veryLightGray,
    marginTop: 0,
    marginBottom: 12,
  },
  calendarModeSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noPeriodDay: {
    // hollow circle
    height: 10,
    width: 10,
    border: `2px solid ${AppColors.text.veryLightGray}`,
    borderRadius: '50%',
  },
  noDataDay: {
    height: 6,
    width: 6,
    backgroundColor: 'white',
  },
  predictedPeriodDay: {
    // hollow circle
    height: 6,
    width: 6,
    border: `1px solid ${AppColors.colorMap.veryBad}`,
    borderRadius: '50%',
  },
  filledOutDay: {
    height: 6,
    width: 6,
    backgroundColor: AppColors.main,
    shapeOutside: 'circle()',
    clipPath: 'circle()',
  },
  filledOutDayInPeriodFillingMode: {
    composes: '$filledOutDay',
    height: 10,
    width: 10,
  },
  bloodDrop: {
    height: 7,
    width: 7,
  },
  specificDayModalDescription: {
    marginBottom: 0,
    color: AppColors.text.veryLightGray,
  },
  specificDayModalTitle: {
    marginTop: 0,
  },
  specificDayModalBigValue: {
    margin: 0,
    marginTop: -5,
    fontWeight: 'bold',
    fontSize: 19,
    '& svg': {
      height: 13,
    },
    '& svg:first-child': {
      marginLeft: 10,
    },
  },
  specificDayModalValue: {
    margin: 0,
    whiteSpace: 'pre-line',
  },
});

const CalendarWeekBar: React.FC<{
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isOpen: boolean) => void;
  calendarMode: 'calendar' | 'periodFilling';
  setCalendarMode: (mode: 'calendar' | 'periodFilling') => void;
  onValidateFillingFirstPeriods: () => void;
}> = ({ isCalendarOpen, setIsCalendarOpen, calendarMode, setCalendarMode, onValidateFillingFirstPeriods }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { value: shouldAskForPeriodPreference } = usePreferences(Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_PERIOD);
  const shouldAskForPeriod = shouldAskForPeriodPreference !== false;

  const { getSpecificDailyStatus, fillPeriodDays, removePeriodFromDay, probableNextPeriodDates, cycleHistory } =
    useSymptomTrackingData();
  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  const numberOfCycles = cycleHistory.length;

  const [specificDay, setSpecificDay] = useState<Dayjs>();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  const specificDayData = specificDay ? getSpecificDailyStatus(specificDay) : undefined;

  const weekDays = Array.from({ length: 7 }).map((_, i) => {
    return today.add(i - 1, 'day');
  });

  const GENERAL_STATE_MAP: { [key: number]: { value: string; color: string } } = {
    0: { value: t('symptomTracking.survey.generalState.veryBad'), color: AppColors.colorMap.veryBad },
    1: { value: t('symptomTracking.survey.generalState.bad'), color: AppColors.colorMap.bad },
    2: { value: t('symptomTracking.survey.generalState.average'), color: AppColors.colorMap.average },
    3: { value: t('symptomTracking.survey.generalState.good'), color: AppColors.colorMap.good },
    4: { value: t('symptomTracking.survey.generalState.veryGood'), color: AppColors.colorMap.veryGood },
  };
  const MENSTRUATION_MAP: { [key: number]: { value: string; color: string } } = {
    0: { value: t('symptomTracking.survey.menstruation.no'), color: AppColors.colorMap.veryGood },
    1: { value: t('symptomTracking.survey.menstruation.low'), color: AppColors.colorMap.good },
    2: { value: t('symptomTracking.survey.menstruation.medium'), color: AppColors.colorMap.average },
    3: { value: t('symptomTracking.survey.menstruation.high'), color: AppColors.colorMap.bad },
    4: { value: t('symptomTracking.survey.menstruation.veryHigh'), color: AppColors.colorMap.veryBad },
  };
  const PAIN_MAP: { [key: number]: { value: string; color: string } } = {
    0: { value: t('symptomTracking.survey.pain.none'), color: AppColors.colorMap.veryGood },
    1: { value: t('symptomTracking.survey.pain.light'), color: AppColors.colorMap.good },
    2: { value: t('symptomTracking.survey.pain.moderate'), color: AppColors.colorMap.average },
    3: { value: t('symptomTracking.survey.pain.strong'), color: AppColors.colorMap.bad },
    4: { value: t('symptomTracking.survey.pain.intense'), color: AppColors.colorMap.veryBad },
  };

  const computeDayStatus = (date: Dayjs) => {
    const isPeriodPredicted =
      probableNextPeriodDates &&
      date.isSameOrAfter(probableNextPeriodDates.start, 'days') &&
      date.isSameOrBefore(probableNextPeriodDates.end, 'days');
    if (date.isBefore(today, 'day')) {
      // yesterday: filled out if there is data, color depends on the period
      const status = getSpecificDailyStatus(date);
      if (status) {
        return status.menstruation_amount === 0 ? styles.dotGreen : styles.dotRed;
      }
    } else if (date.isSame(today, 'day')) {
      // today: filled out if there is data, color depends on the period; outlined if no data and period prediction
      const status = getSpecificDailyStatus(date);
      if (status) {
        return status.menstruation_amount === 0 ? styles.dotGreen : styles.dotRed;
      }
      if (isPeriodPredicted) {
        return styles.dotRedOutlined;
      }
    } else if (isPeriodPredicted)
      // days in the future: outlined if period is predicted
      return styles.dotRedOutlined;

    return styles.dot;
  };

  const previousMonth = () => {
    // note: we add 2 days to exclude any edge-effects with timezones
    setSelectedDate(selectedDate.startOf('month').subtract(1, 'month').add(2, 'days'));
  };
  const nextMonth = () => {
    // note: we add 2 days to exclude any edge-effects with timezones
    setSelectedDate(selectedDate.startOf('month').add(1, 'month').add(2, 'days'));
  };

  function renderMenstruationLevel(value: number) {
    return [...Array(value)].map((_, i) => (
      <Drop key={i} fill={AppColors.colorMap.veryBad} className={styles.bloodDrop} />
    ));
  }

  function getDateStyle(date: Dayjs) {
    if (date.month() !== today.month()) return styles.dateNumberOtherMonth;

    if (date.date() < today.date()) return styles.dateNumberPast;
    if (date.date() > today.date()) return styles.dateNumberFuture;

    return styles.dateNumberToday;
  }

  const renderDateCell = (current: Dayjs) => {
    const dayData = getSpecificDailyStatus(current);
    return calendarMode === 'calendar' ? (
      <div className={styles.dateCellContainer}>
        <div className={styles.dateCellTopRow}>{dayData && dayData.comment && <InfoCircleFilled />}</div>
        <div className={getDateStyle(current)}>{current.date()}</div>
        {/* eslint-disable-next-line no-nested-ternary */}
        {dayData ? (
          <div className={styles.dayDots}>
            <div className={styles.dateCellBottomRow}>
              {dayData.menstruation_amount === 0 ? (
                <div
                  className={styles.filledOutDay}
                  data-testid={`tracking.filled-out-day-${current.format('YYYY-MM-DD')}`}
                />
              ) : (
                renderMenstruationLevel(dayData.menstruation_amount)
              )}
            </div>
          </div>
        ) : probableNextPeriodDates &&
          current.isSameOrAfter(probableNextPeriodDates.start, 'days') &&
          current.isSameOrBefore(probableNextPeriodDates.end, 'days') ? (
          <div className={styles.predictedPeriodDay} />
        ) : (
          <div className={styles.noDataDay} />
        )}
      </div>
    ) : (
      <div className={styles.dateCellContainer}>
        <div className={getDateStyle(current)}>{current.date()}</div>
        <div className={styles.dayDots}>
          <div className={styles.dateCellBottomRowWhenFillingPeriod}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {dayData ? (
              dayData.menstruation_amount === 0 ? (
                <div
                  className={styles.filledOutDayInPeriodFillingMode}
                  data-testid={`tracking.filled-out-day-${current.format('YYYY-MM-DD')}`}
                />
              ) : (
                renderMenstruationLevel(dayData.menstruation_amount)
              )
            ) : (
              <div className={styles.noPeriodDay} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCalendarHeader = ({ value }: { value: Dayjs }) => {
    return (
      <div className={styles.calendarHeader}>
        <GGButton onClick={previousMonth}>
          <LeftOutlined />
        </GGButton>
        {value.format('MMMM YYYY')}
        <GGButton onClick={nextMonth}>
          <RightOutlined />
        </GGButton>
      </div>
    );
  };

  const renderModalBigStat = (
    title: string,
    value: number | null,
    map: { [key: number]: { value: string; color: string } },
    valueExtra?: React.ReactNode,
  ) => {
    const mapValue = value !== null ? map[value] : { value: '-', color: AppColors.text.veryLightGray };
    return (
      <>
        <p className={styles.specificDayModalDescription}>{title}</p>
        <p className={styles.specificDayModalBigValue} style={{ color: mapValue.color }}>
          {mapValue.value} {valueExtra}
        </p>
      </>
    );
  };

  const onDateSelected = (date: Dayjs) => {
    setSelectedDate(date);
    if (calendarMode === 'calendar') {
      if (date <= today) setSpecificDay(date);
      amplitude.track('tracking.calendar_day_selected');
      posthog.capture('tracking.calendar_day_selected');
    } else if (date <= today) {
      const dayData = getSpecificDailyStatus(date);
      if (dayData && dayData.menstruation_amount > 0) {
        removePeriodFromDay(date);
      } else {
        const theDayBefore = getSpecificDailyStatus(date.subtract(1, 'day'));
        if (theDayBefore && theDayBefore.menstruation_amount > 0) fillPeriodDays(date, 1);
        else fillPeriodDays(date, 5);
        posthog.capture('tracking.period_quickfilled_one_cycle');
      }
    }
  };

  const toggleCalendar = () => {
    if (!isCalendarOpen) {
      setCalendarMode('calendar');
    }
    setIsCalendarOpen(!isCalendarOpen);
  };

  return (
    <div style={{ width: '100%' }}>
      <Collapse
        activeKey={isCalendarOpen ? ['calendar'] : []}
        ghost
        className={styles.calendarCollapse}
        items={[
          {
            key: 'calendar',
            showArrow: false,
            children: (
              <>
                <Calendar
                  className={styles.calendar}
                  fullscreen={false}
                  fullCellRender={renderDateCell}
                  headerRender={renderCalendarHeader}
                  onSelect={onDateSelected}
                  value={selectedDate}
                />
                <div className={styles.calendarModeSelector}>
                  {calendarMode === 'periodFilling' ? (
                    <>
                      {numberOfCycles < 3 && (
                        <>{t('symptomTracking.cycles.fillMoreCycles', { count: 3 - numberOfCycles })}</>
                      )}
                      <GGButton
                        type="primary"
                        disabled={numberOfCycles < 3}
                        size="large"
                        onClick={onValidateFillingFirstPeriods}
                      >
                        <CheckOutlined />
                        {t('symptomTracking.cycles.validate')}
                      </GGButton>
                    </>
                  ) : (
                    shouldAskForPeriod && (
                      <>
                        <p className={styles.calendarHelper}>
                          <BulbFilled /> {t('symptomTracking.cycles.calendarHelper')}
                        </p>
                        <GGButton
                          onClick={() => {
                            setCalendarMode('periodFilling');
                            posthog.capture('tracking.period_quickfill_started');
                          }}
                        >
                          <Drop fill={AppColors.colorMap.veryBad} className={styles.bloodDrop} />
                          {t('symptomTracking.cycles.fillPastCycles')}
                        </GGButton>
                      </>
                    )
                  )}
                </div>
              </>
            ),
          },
        ]}
      />
      <div className={styles.weekBar}>
        {weekDays.map((day, idx) => (
          <div
            className={styles.dayContainer}
            key={idx}
            onClick={() => {
              if (day.date() <= today.date()) {
                setSpecificDay(day);
              }
            }}
          >
            <span className={styles.dayLabel}>{day.format('dd').slice(0, 2)}</span>
            <span
              className={
                // eslint-disable-next-line no-nested-ternary
                day.date() === yesterday.date()
                  ? `${styles.date} ${styles.datePast}`
                  : day.date() === today.date()
                    ? `${styles.date} ${styles.dateToday}`
                    : styles.date
              }
            >
              {day.date()}
            </span>
            <span className={computeDayStatus(day)} />
          </div>
        ))}
        <button
          className={styles.calendarIconContainer}
          onClick={toggleCalendar}
          aria-expanded={isCalendarOpen}
          aria-label="Toggle calendar view"
          type="button"
        >
          <CalendarOutlined />
          <DownOutlined style={{ fontSize: 15, transform: isCalendarOpen ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>
      <Modal open={specificDay !== undefined} onCancel={() => setSpecificDay(undefined)} footer={<span />}>
        <h2 className={styles.specificDayModalTitle}>{specificDay?.format('LL')}</h2>
        {specificDayData === undefined && (
          <>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('symptomTracking.noDataForDay')} />
            <GGButton
              type="primary"
              size="large"
              block
              onClick={() => {
                navigate({
                  pathname: routes.symptomTrackingSurvey.route,
                  search: createSearchParams({
                    date: (specificDay as Dayjs).format('YYYY-MM-DD'),
                  }).toString(),
                });
                amplitude.track('tracking.survey.started', {
                  source:
                    specificDay?.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
                      ? 'calendar_today'
                      : 'calendar_past_day',
                });
                posthog.capture('tracking.survey.started', {
                  source:
                    specificDay?.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
                      ? 'calendar_today'
                      : 'calendar_past_day',
                });
              }}
            >
              {t('symptomTracking.actions.fillDailySurvey')}
            </GGButton>
          </>
        )}
        {specificDayData !== undefined && (
          <div>
            {renderModalBigStat(
              t('symptomTracking.compare.generalState'),
              specificDayData?.general_state,
              GENERAL_STATE_MAP,
            )}
            {renderModalBigStat(
              t('symptomTracking.compare.period'),
              specificDayData?.menstruation_amount,
              MENSTRUATION_MAP,
              <>
                {Array.from({ length: specificDayData?.menstruation_amount }).map((_, i) => (
                  <Drop key={i} fill={AppColors.colorMap.veryBad} />
                ))}
              </>,
            )}
            {renderModalBigStat(t('symptomTracking.compare.pain'), specificDayData?.pain_level, PAIN_MAP)}
            <p className={styles.specificDayModalDescription}>{t('symptomTracking.compare.symptoms')}</p>
            <p className={styles.specificDayModalValue}>
              {specificDayData?.symptoms.length > 0
                ? specificDayData?.symptoms.map(symptom => <SymptomTag key={symptom} symptom={symptom} />)
                : t('symptomTracking.calendar.noSymptomsReported')}
            </p>
            {specificDayData?.extra_symptoms && (
              <>
                <p className={styles.specificDayModalDescription}>{t('symptomTracking.graphs.otherSymptoms')}</p>
                <p className={styles.specificDayModalValue}>{specificDayData?.extra_symptoms}</p>
              </>
            )}
            {specificDayData?.comment && (
              <>
                <p className={styles.specificDayModalDescription}>{t('symptomTracking.graphs.notes')}</p>
                <p className={styles.specificDayModalValue}>{specificDayData?.comment}</p>
              </>
            )}
            <GGButton
              type="primary"
              size="large"
              block
              style={{ marginTop: 16 }}
              onClick={() => {
                navigate({
                  pathname: routes.symptomTrackingSurvey.route,
                  search: createSearchParams({
                    date: (specificDay as Dayjs).format('YYYY-MM-DD'),
                    edit: 'true',
                  }).toString(),
                });
                amplitude.track('tracking.survey.edit_started', {
                  source:
                    specificDay?.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
                      ? 'calendar_today'
                      : 'calendar_past_day',
                });
                posthog.capture('tracking.survey.edit_started', {
                  source:
                    specificDay?.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
                      ? 'calendar_today'
                      : 'calendar_past_day',
                });
              }}
            >
              {t('symptomTracking.actions.editDailySurvey')}
            </GGButton>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CalendarWeekBar;
